import React, { useState, useEffect, useCallback } from 'react';
import {
  MousePointerClick,
  RefreshCw,
  Globe,
  Loader2,
  ExternalLink,
  Users,
  Package,
  Store,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  fetchProductClicks,
  fetchProductClicksOverview,
  fetchCountriesForDropdown,
  ProductClickItem,
  ProductClicksOverview,
} from '../services/api.ts';
import './ProductClicks.css';

interface Country {
  id: number;
  name: string;
}

type DateRange = '7' | '30' | '90';

const ProductClicks: React.FC = () => {
  const [clicks, setClicks] = useState<ProductClickItem[]>([]);
  const [overview, setOverview] = useState<ProductClicksOverview | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [dateRange, setDateRange] = useState<DateRange>('30');
  const [filterCountryId, setFilterCountryId] = useState<number | undefined>(undefined);

  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    loadData();
  }, [dateRange, filterCountryId, page]);

  const loadCountries = async () => {
    try {
      const data = await fetchCountriesForDropdown();
      setCountries(data);
    } catch {
      // ignore
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const days = parseInt(dateRange);
      const [clicksData, overviewData] = await Promise.all([
        fetchProductClicks(days, filterCountryId, pageSize, page * pageSize),
        fetchProductClicksOverview(days, filterCountryId),
      ]);
      setClicks(clicksData.items);
      setTotal(clicksData.total);
      setOverview(overviewData);
    } catch (e: any) {
      setError(e.message || 'Failed to load product clicks');
    } finally {
      setLoading(false);
    }
  }, [dateRange, filterCountryId, page]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) +
           ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const truncateUrl = (url: string, max: number = 50) => {
    try {
      const u = new URL(url);
      const display = u.hostname + u.pathname;
      return display.length > max ? display.slice(0, max) + '…' : display;
    } catch {
      return url.length > max ? url.slice(0, max) + '…' : url;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="pc-container">
      {/* Header */}
      <div className="pc-header">
        <div className="pc-header-left">
          <MousePointerClick size={28} className="pc-header-icon" />
          <div>
            <h1>Product Clicks</h1>
            <p>Track which product links users are clicking</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="pc-filter-bar">
        <div className="pc-filter-group">
          <Globe size={16} />
          <select
            value={filterCountryId || ''}
            onChange={(e) => {
              setFilterCountryId(e.target.value ? Number(e.target.value) : undefined);
              setPage(0);
            }}
          >
            <option value="">All Countries</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="pc-filter-group">
          <select value={dateRange} onChange={(e) => { setDateRange(e.target.value as DateRange); setPage(0); }}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
        <button className="pc-btn pc-btn-secondary" onClick={loadData}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <span className="pc-count">{total.toLocaleString()} click{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Overview cards */}
      {overview && (
        <div className="pc-stats-grid">
          <div className="pc-stat-card">
            <div className="pc-stat-icon clicks"><TrendingUp size={22} /></div>
            <div className="pc-stat-info">
              <span className="pc-stat-value">{overview.total_clicks.toLocaleString()}</span>
              <span className="pc-stat-label">Total Clicks</span>
            </div>
          </div>
          <div className="pc-stat-card">
            <div className="pc-stat-icon users"><Users size={22} /></div>
            <div className="pc-stat-info">
              <span className="pc-stat-value">{overview.unique_users.toLocaleString()}</span>
              <span className="pc-stat-label">Unique Users</span>
            </div>
          </div>
          <div className="pc-stat-card">
            <div className="pc-stat-icon products"><Package size={22} /></div>
            <div className="pc-stat-info">
              <span className="pc-stat-value">{overview.unique_products.toLocaleString()}</span>
              <span className="pc-stat-label">Unique Products</span>
            </div>
          </div>
          <div className="pc-stat-card">
            <div className="pc-stat-icon retailers"><Store size={22} /></div>
            <div className="pc-stat-info">
              <span className="pc-stat-value">{overview.unique_retailers.toLocaleString()}</span>
              <span className="pc-stat-label">Unique Retailers</span>
            </div>
          </div>
        </div>
      )}

      {/* Top products & top retailers */}
      {overview && (overview.top_products.length > 0 || overview.top_retailers.length > 0) && (
        <div className="pc-top-grid">
          {overview.top_products.length > 0 && (
            <div className="pc-top-card">
              <h3><Package size={16} /> Top Products</h3>
              <ul>
                {overview.top_products.map((p, i) => (
                  <li key={p.itemkits_id}>
                    <span className="pc-rank">#{i + 1}</span>
                    <span className="pc-top-name">{p.name}</span>
                    <span className="pc-top-count">{p.clicks.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overview.top_retailers.length > 0 && (
            <div className="pc-top-card">
              <h3><Store size={16} /> Top Retailers</h3>
              <ul>
                {overview.top_retailers.map((r, i) => (
                  <li key={r.retailer_id}>
                    <span className="pc-rank">#{i + 1}</span>
                    <span className="pc-top-name">{r.name}</span>
                    <span className="pc-top-count">{r.clicks.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="pc-loading"><Loader2 size={24} className="pc-spin" /> Loading...</div>
      ) : error ? (
        <div className="pc-error">{error}</div>
      ) : clicks.length === 0 ? (
        <div className="pc-empty">
          <MousePointerClick size={48} />
          <h3>No product clicks yet</h3>
          <p>Product clicks will appear here when users tap the "View" button on product details.</p>
        </div>
      ) : (
        <>
          <div className="pc-table-container">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Product</th>
                  <th>Retailer</th>
                  <th>URL</th>
                  <th>Country</th>
                  <th>Platform</th>
                  <th>Clicked At</th>
                </tr>
              </thead>
              <tbody>
                {clicks.map((click) => (
                  <tr key={click.id}>
                    <td className="pc-user-cell">{click.user_name}</td>
                    <td className="pc-product-cell">{click.product_name}</td>
                    <td>
                      <span className="pc-retailer-badge">{click.retailer_name}</span>
                    </td>
                    <td className="pc-url-cell">
                      <a href={click.url} target="_blank" rel="noopener noreferrer" title={click.url}>
                        {truncateUrl(click.url)}
                        <ExternalLink size={12} />
                      </a>
                    </td>
                    <td>
                      <span className="pc-country-badge">{click.country_name}</span>
                    </td>
                    <td className="pc-platform-cell">
                      {click.platform && (
                        <span className={`pc-platform-badge ${click.platform.toLowerCase()}`}>
                          {click.platform}
                        </span>
                      )}
                    </td>
                    <td className="pc-date-cell">{formatDate(click.clicked_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pc-pagination">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft size={16} /> Previous
              </button>
              <span>Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductClicks;
